import { motion } from "framer-motion";
import { PenLine, CreditCard, Printer, PackageCheck, Send, Phone } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Logo } from "@/components/Header";
import { useI18n } from "@/i18n";

const ICONS = [PenLine, CreditCard, Printer, PackageCheck];

export const HowItWorks = () => {
  const { t } = useI18n();
  return (
    <section id="how" className="rpm-section bg-white py-16 sm:py-20 lg:py-24" data-testid="how-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="rpm-label mb-3">{t("how.label")}</div>
        <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("how.title")}</h2>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {t("how.steps").map(([title, text], i) => {
            const Icon = ICONS[i];
            return (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: i * 0.07 }}
                className="rpm-card rpm-card-hover relative p-6"
                data-testid={`how-step-${i + 1}`}
              >
                <div className="mb-8 flex items-center justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl border-[1.5px] border-black">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="font-display text-5xl font-bold text-zinc-200">0{i + 1}</span>
                </div>
                <h3 className="font-display text-lg font-semibold">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-600">{text}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const Faq = () => {
  const { t } = useI18n();
  return (
    <section id="faq" className="rpm-section border-t border-zinc-200 bg-white py-16 sm:py-20 lg:py-24" data-testid="faq-section">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.8fr_1.2fr]">
        <div>
          <div className="rpm-label mb-3">{t("faq.label")}</div>
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("faq.title")}</h2>
          <p className="mt-3 text-sm text-zinc-600">{t("faq.sub")}</p>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {t("faq.items").map(([q, a], i) => (
            <AccordionItem key={q} value={`q${i}`} className="border-zinc-200">
              <AccordionTrigger className="py-5 text-left font-display text-base font-semibold hover:no-underline sm:text-lg" data-testid={`faq-item-${i}-trigger`}>
                {q}
              </AccordionTrigger>
              <AccordionContent className="pb-5 text-sm leading-relaxed text-zinc-700">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

export const Contacts = ({ telegramUrl, phone }) => {
  const { t } = useI18n();
  return (
    <section id="contacts" className="rpm-section bg-white pb-16 sm:pb-20 lg:pb-24" data-testid="contacts-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="rpm-grid-bg relative overflow-hidden rounded-[28px] border-[1.5px] border-black p-8 sm:p-12">
          <div className="grid items-center gap-8 lg:grid-cols-[1.3fr_1fr]">
            <div>
              <div className="rpm-label mb-3">{t("contacts.label")}</div>
              <h2 className="font-display text-3xl font-bold tracking-tight sm:text-5xl">{t("contacts.title")}</h2>
              <p className="mt-4 max-w-lg text-zinc-700">{t("contacts.sub")}</p>
            </div>
            <div className="flex flex-col gap-3">
              {telegramUrl && (
                <a href={telegramUrl} target="_blank" rel="noopener noreferrer" className="rpm-btn rpm-btn-orange h-14 text-base" data-testid="contacts-telegram-button">
                  <Send className="h-5 w-5" /> {t("contacts.telegram")}
                </a>
              )}
              {phone && (
                <a href={`tel:${phone.replace(/\s/g, "")}`} className="rpm-btn rpm-btn-outline h-14 text-base" data-testid="contacts-phone-button">
                  <Phone className="h-5 w-5" /> {phone}
                </a>
              )}
              <a href="#order" className="rpm-btn rpm-btn-lime h-14 text-base" data-testid="contacts-order-button">
                {t("contacts.order")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  const { t } = useI18n();
  return (
    <footer className="border-t border-zinc-200 bg-white" data-testid="site-footer">
      <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-10 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <Logo />
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-600">
          {["products", "order", "faq", "contacts"].map((id) => (
            <a key={id} href={`/#${id}`} className="hover:text-black">
              {t(`nav.${id}`)}
            </a>
          ))}
        </div>
        <div className="font-mono-rpm text-xs text-zinc-500">© {new Date().getFullYear()} RPM 3D PRINT</div>
      </div>
    </footer>
  );
};
